import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import {
    Package,
    Plus,
    Search,
    Filter,
    Eye,
    Edit,
    Trash2,
    FileText,
    Upload,
    Archive,
    Download,
    QrCode,
    CheckCircle,
    XCircle,
    MapPin,
    Clock,
    Wrench
} from 'lucide-react';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import ModalMantenimientoActivo from '../components/common/ModalMantenimientoActivo';
import Loading from '../components/common/Loading';
import DownloadProgress from '../components/common/DownloadProgress';
import { activosService } from '../services/activos';
import { categoriasService } from '../services/categorias';
import { ubicacionesService } from '../services/ubicaciones';
import toast from 'react-hot-toast';
import { AlertCircle} from 'lucide-react';
import QRDisplay from '../components/common/QRDisplay';
import { useAuth } from '../hooks/useAuth';

const ActivosPage = () => {
    const { isLector } = useAuth();
    const [activos, setActivos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [ubicaciones, setUbicaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [ubicacionFilter, setUbicacionFilter] = useState('');
    const [selectedActivos, setSelectedActivos] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('create');
    const [selectedActivo, setSelectedActivo] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [selectedActivoForQR, setSelectedActivoForQR] = useState(null);
    const [showMantenimientoModal, setShowMantenimientoModal] = useState(false);
    const [selectedActivoMantenimiento, setSelectedActivoMantenimiento] = useState(null);
    const [downloadProgress, setDownloadProgress] = useState({
        isDownloading: false,
        type: null,
        progress: 0,
        error: null,
        success: false
    });
    // Estados de paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    // Estados para estadísticas
    const [statsData, setStatsData] = useState({
        enServicio: 0,
        fueraServicio: 0,
        conQR: 0
    });
    const [formData, setFormData] = useState({
        code: '',
        id_netsuite: '',
        name: '',
        factory: '',
        model: '',
        outservice: false,
        etiquetado: false,
        hours: '',
        visible: true,
        urlcodeqr: '',
        timeshopping: '',
        categoria: '',
        ubicacion_id: '',
        precio_compra: '',
        vida_util_anios: '',
        valor_residual: '',
        es_nuevo_ingreso: false
    });
    const [downloadingQR, setDownloadingQR] = useState(false);
    const [showQRDownloadModal, setShowQRDownloadModal] = useState(false);
    const fileInputRef = useRef(null);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);


    // Cargar activos, categorías y ubicaciones al montar el componente
    useEffect(() => {
        loadActivos();
        loadCategorias();
        loadUbicaciones();
    }, []);

    // Cargar categorías desde la API
    const loadCategorias = async () => {
        try {
            const data = await categoriasService.getActivas();
            setCategorias(data);
        } catch (error) {
            console.error('Error al cargar categorías:', error);
        }
    };

    // Cargar ubicaciones desde la API
    const loadUbicaciones = async () => {
        try {
            console.log('Intentando cargar ubicaciones...');
            const data = await ubicacionesService.getUbicacionesSelect(true);
            console.log('Ubicaciones cargadas:', data);
            setUbicaciones(data);
        } catch (error) {
            console.error('Error detallado al cargar ubicaciones:', error);
            console.error('Response:', error.response);
            console.error('Status:', error.response?.status);
            console.error('Data:', error.response?.data);
        }
    };

    // Cargar activos desde la API
    const loadActivos = async (search = '', page = currentPage, limit = itemsPerPage, category = categoryFilter, status = statusFilter, ubicacion = ubicacionFilter) => {
        try {
            setLoading(true);
            const skip = (page - 1) * limit;

            const params = {
                search,
                skip,
                limit
            };

            // Agregar filtros al backend si están definidos
            if (category) {
                params.categoria = category;
            }
            if (status) {
                params.outservice = status === 'fuera_servicio' ? 'true' : 'false';
            }
            if (ubicacion) {
                params.ubicacion_id = ubicacion;
            }

            const data = await activosService.getAll(params);

            let allItems = [];
            let total = 0;

            // Si el servicio retorna un objeto con items y total
            if (data && typeof data === 'object' && data.items) {
                allItems = data.items;
                total = data.total || data.items.length;
            } else if (Array.isArray(data)) {
                // Si retorna un array directamente (retrocompatibilidad)
                allItems = data;
                total = data.length;
            }

            // Calcular estadísticas de la página actual
            const stats = {
                enServicio: allItems.filter(a => !a.outservice).length,
                fueraServicio: allItems.filter(a => a.outservice).length,
                conQR: allItems.filter(a => a.urlcodeqr).length
            };
            setStatsData(stats);

            setActivos(allItems);
            setTotalItems(total);
        } catch (error) {
            console.error('Error al cargar activos:', error);
            toast.error('Error al cargar activos');
        } finally {
            setLoading(false);
        }
    };

    // Buscar activos
    const handleSearch = async () => {
        setCurrentPage(1); // Resetear a página 1 al buscar
        await loadActivos(searchTerm, 1);
    };

    // Manejar cambio de página
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        loadActivos(searchTerm, newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Manejar cambio de items por página
    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        // Pasar el nuevo límite directamente porque setState es asíncrono
        loadActivos(searchTerm, 1, newItemsPerPage, categoryFilter, statusFilter, ubicacionFilter);
    };

    // Limpiar búsqueda
    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            setCurrentPage(1); // Resetear a página 1
            if (searchTerm !== '') {
                loadActivos(searchTerm, 1, itemsPerPage, categoryFilter, statusFilter, ubicacionFilter);
            } else {
                loadActivos('', 1, itemsPerPage, categoryFilter, statusFilter, ubicacionFilter);
            }
        }, 500);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    // Recargar cuando cambien los filtros
    useEffect(() => {
        setCurrentPage(1);
        loadActivos(searchTerm, 1, itemsPerPage, categoryFilter, statusFilter, ubicacionFilter);
    }, [categoryFilter, statusFilter, ubicacionFilter]);

    // Los activos ya vienen filtrados de loadActivos
    const filteredActivos = activos;

    // Manejar selección de activos individuales
    const handleSelectActivo = (activoId) => {
        setSelectedActivos(prev => 
            prev.includes(activoId) 
                ? prev.filter(id => id !== activoId)
                : [...prev, activoId]
        );
    };

    // Seleccionar/deseleccionar todos
    const handleSelectAll = () => {
        if (selectedActivos.length === filteredActivos.length) {
            setSelectedActivos([]);
        } else {
            setSelectedActivos(filteredActivos.map(activo => activo.id));
        }
    };

    // Descargar QR de activos seleccionados
    const handleDownloadSelectedQRs = async () => {
        if (selectedActivos.length === 0) {
            toast.error('No hay activos seleccionados');
            return;
        }

        setDownloadProgress({
            isDownloading: true,
            type: 'multiple',
            progress: 0,
            error: null,
            success: false
        });

        try {
            const selectedActivosData = activos.filter(a => selectedActivos.includes(a.id));
            
            for (let i = 0; i < selectedActivosData.length; i++) {
                const activo = selectedActivosData[i];
                const progress = Math.round((i / selectedActivosData.length) * 100);
                
                setDownloadProgress(prev => ({ ...prev, progress }));
                
                await activosService.downloadQR(activo.id);
                
                // Pequeña pausa entre descargas
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            setDownloadProgress(prev => ({ 
                ...prev, 
                progress: 100, 
                success: true, 
                isDownloading: false 
            }));
            
            toast.success(`${selectedActivos.length} códigos QR descargados correctamente`);
            setSelectedActivos([]);
            
            setTimeout(() => {
                setDownloadProgress({ isDownloading: false, type: null, progress: 0, error: null, success: false });
            }, 3000);

        } catch (error) {
            console.error('Error downloading selected QRs:', error);
            setDownloadProgress({
                isDownloading: false,
                type: 'multiple',
                progress: 0,
                error: 'Error al descargar códigos QR seleccionados',
                success: false
            });
            toast.error('Error al descargar códigos QR seleccionados');
        }
    };

     const handleDownloadTemplate = async () => {
        try {
            setDownloadingTemplate(true);
            
            // Descargar desde carpeta template local
            const result = await activosService.downloadTemplate();
            toast.success(`Plantilla descargada: ${result.filename}`);
            
        } catch (error) {
            console.error('Error downloading template:', error);
            
            // Fallback: mostrar mensaje con ubicación manual
            toast.error('Error al descargar. Encuentra la plantilla en la carpeta "template" del proyecto');
            
            // Opcionalmente, abrir en nueva ventana
            try {
                window.open('/template/Plantilla_Activos_Argus.xlsx', '_blank');
            } catch (fallbackError) {
                console.error('Fallback también falló:', fallbackError);
            }
        } finally {
            setDownloadingTemplate(false);
        }
    };

      // Función para descargar QR individual
    const handleDownloadQR = async (activo) => {
        try {
            setDownloadProgress({
                isDownloading: true,
                type: 'single',
                progress: 0,
                error: null,
                success: false
            });

            // Simular progreso
            setDownloadProgress(prev => ({ ...prev, progress: 50 }));
            
            await activosService.downloadQR(activo.id);
            
            setDownloadProgress(prev => ({ ...prev, progress: 100, success: true, isDownloading: false }));
            toast.success(`QR de ${activo.code} descargado correctamente`);
            
            // Limpiar progreso después de 3 segundos
            setTimeout(() => {
                setDownloadProgress({ isDownloading: false, type: null, progress: 0, error: null, success: false });
            }, 3000);
            
        } catch (error) {
            console.error('Error downloading QR:', error);
            setDownloadProgress({
                isDownloading: false,
                type: 'single',
                progress: 0,
                error: 'Error al descargar código QR',
                success: false
            });
            toast.error('Error al descargar código QR');
        }
    };


 // Función para descargar todos los QR en PDF
    // Función para descargar todos los QR en PDF
    const handleDownloadAllQRsPDF = async () => {
        try {
            setDownloadProgress({
                isDownloading: true,
                type: 'pdf',
                progress: 0,
                error: null,
                success: false
            });

            // Simular progreso gradual
            const intervals = [20, 40, 60, 80];
            for (let i = 0; i < intervals.length; i++) {
                setTimeout(() => {
                    setDownloadProgress(prev => ({ ...prev, progress: intervals[i] }));
                }, (i + 1) * 500);
            }

            const result = await activosService.downloadAllQRsPDF();
            
            setDownloadProgress(prev => ({ 
                ...prev, 
                progress: 100, 
                success: true, 
                isDownloading: false 
            }));
            
            toast.success(`PDF generado: ${result.filename}`);
            setShowQRDownloadModal(false);
            
            // Limpiar progreso después de 3 segundos
            setTimeout(() => {
                setDownloadProgress({ isDownloading: false, type: null, progress: 0, error: null, success: false });
            }, 3000);
            
        } catch (error) {
            console.error('Error downloading QR PDF:', error);
            setDownloadProgress({
                isDownloading: false,
                type: 'pdf',
                progress: 0,
                error: 'Error al generar PDF de códigos QR',
                success: false
            });
            toast.error('Error al generar PDF de códigos QR');
        }
    };
  // Función para descargar todos los QR en ZIP
   const handleDownloadAllQRsZIP = async () => {
        try {
            setDownloadProgress({
                isDownloading: true,
                type: 'zip',
                progress: 0,
                error: null,
                success: false
            });

            // Simular progreso gradual
            const intervals = [15, 30, 45, 60, 75, 90];
            for (let i = 0; i < intervals.length; i++) {
                setTimeout(() => {
                    setDownloadProgress(prev => ({ ...prev, progress: intervals[i] }));
                }, (i + 1) * 400);
            }

            const result = await activosService.downloadAllQRsZIP();
            
            setDownloadProgress(prev => ({ 
                ...prev, 
                progress: 100, 
                success: true, 
                isDownloading: false 
            }));
            
            toast.success(`ZIP generado: ${result.filename}`);
            setShowQRDownloadModal(false);
            
            // Limpiar progreso después de 3 segundos
            setTimeout(() => {
                setDownloadProgress({ isDownloading: false, type: null, progress: 0, error: null, success: false });
            }, 3000);
            
        } catch (error) {
            console.error('Error downloading QR ZIP:', error);
            setDownloadProgress({
                isDownloading: false,
                type: 'zip',
                progress: 0,
                error: 'Error al generar ZIP de códigos QR',
                success: false
            });
            toast.error('Error al generar ZIP de códigos QR');
        }
    };



    // Limpiar estado de progreso
    const clearDownloadProgress = () => {
        setDownloadProgress({ 
            isDownloading: false, 
            type: null, 
            progress: 0, 
            error: null, 
            success: false 
        });
    };

    // Crear activo
    const handleCreate = () => {
        setModalType('create');
        setFormData({
            code: '',
            id_netsuite: '',
            name: '',
            factory: '',
            model: '',
            outservice: false,
            etiquetado: false,
                hours: '',
            visible: true,
            urlcodeqr: '',
            timeshopping: '',
            categoria: '',
            ubicacion_id: '',
            precio_compra: '',
            vida_util_anios: '',
            valor_residual: '',
            es_nuevo_ingreso: true
        });
        setShowModal(true);
    };

    // Editar activo
    const handleEdit = async (activo) => {
        setModalType('edit');
        setSelectedActivo(activo);
        setFormData({
            code: activo.code,
            id_netsuite: activo.id_netsuite || '',
            name: activo.name,
            factory: activo.factory,
            model: activo.model,
            outservice: activo.outservice,
            etiquetado: activo.etiquetado || false,
            hours: activo.hours || '',
            visible: activo.visible,
            urlcodeqr: activo.urlcodeqr || '',
            timeshopping: activo.timeshopping ? activo.timeshopping.split('T')[0] : '',
            categoria: activo.categoria || '',
            ubicacion_id: activo.ubicacion_id || '',
            precio_compra: activo.precio_compra ?? '',
            vida_util_anios: activo.vida_util_anios ?? '',
            valor_residual: activo.valor_residual ?? '',
            es_nuevo_ingreso: activo.es_nuevo_ingreso || false
        });
        setShowModal(true);
    };

    // Ver detalles
    const handleView = (activo) => {
        setModalType('view');
        setSelectedActivo(activo);
        setShowModal(true);
    };

    // Eliminar activo
    const handleDelete = async (activo) => {
        if (window.confirm(`¿Estás seguro de eliminar el activo ${activo.code}?`)) {
            try {
                await activosService.delete(activo.id);
                toast.success('Activo eliminado correctamente');
                loadActivos(); // Recargar lista
            } catch (error) {
                console.error('Error al eliminar activo:', error);
                toast.error('Error al eliminar activo');
            }
        }
    };

    // Función para formatear errores de validación
    const formatValidationError = (error) => {
        if (error.response?.data?.detail) {
            // Si el detalle es un array (errores de validación de Pydantic)
            if (Array.isArray(error.response.data.detail)) {
                return error.response.data.detail
                    .map(err => `${err.loc?.join('.')}: ${err.msg}`)
                    .join(', ');
            }
            // Si es un string simple
            return error.response.data.detail;
        }
        return 'Error al guardar activo';
    };

    // Guardar activo (crear o editar)
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Validaciones básicas del lado cliente
            // El código se genera automáticamente en el backend para nuevos activos
            if (!formData.name.trim()) {
                toast.error('El nombre es obligatorio');
                return;
            }
            if (!formData.factory.trim()) {
                toast.error('El fabricante es obligatorio');
                return;
            }
            if (!formData.model.trim()) {
                toast.error('El modelo es obligatorio');
                return;
            }

            // Preparar datos para envío
            const dataToSend = {
                ...formData,
                hours: formData.hours || null,
                urlcodeqr: formData.urlcodeqr || null,
                timeshopping: formData.timeshopping ? `${formData.timeshopping}T00:00:00` : null,
                precio_compra: formData.precio_compra !== '' ? Number(formData.precio_compra) : null,
                vida_util_anios: formData.vida_util_anios !== '' ? parseInt(formData.vida_util_anios, 10) : null,
                valor_residual: formData.valor_residual !== '' ? Number(formData.valor_residual) : null,
            };

            // En modo creación, no enviar el código si está vacío (se genera automáticamente)
            if (modalType === 'create' && !formData.code.trim()) {
                delete dataToSend.code;
            }

            if (modalType === 'create') {
                await activosService.create(dataToSend);
                toast.success('Activo creado correctamente');
            } else if (modalType === 'edit') {
                await activosService.update(selectedActivo.id, dataToSend);
                toast.success('Activo actualizado correctamente');
            }

            setShowModal(false);
            loadActivos(); // Recargar lista
        } catch (error) {
            console.error('Error al guardar activo:', error);
            const errorMessage = formatValidationError(error);
            toast.error(errorMessage);
        }
    };

    // Subir archivo Excel
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            try {
                setLoading(true);
                const result = await activosService.uploadExcel(file);
                toast.success(result.message);
                setShowUploadModal(false);
                loadActivos(); // Recargar lista
            } catch (error) {
                console.error('Error al subir archivo:', error);
                const errorMessage = formatValidationError(error);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        }
    };

    // Exportar Excel
    const handleExport = async () => {
        try {
            await activosService.exportExcel();
            toast.success('Archivo descargado correctamente');
        } catch (error) {
            console.error('Error al exportar archivo:', error);
            toast.error('Error al exportar archivo');
        }
    };

    const openQRModal = (activo) => {
        setSelectedActivoForQR(activo);
        setShowQRModal(true);
    };
    const openMantenimientoModal = (activo) => {
        setSelectedActivoMantenimiento(activo);
        setShowMantenimientoModal(true);
    };

    const getStatusBadge = (activo) => {
        if (activo.outservice) {
            return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircle className="w-3 h-3 mr-1" />
        Fuera de Servicio
      </span>;
        }
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
      <CheckCircle className="w-3 h-3 mr-1" />
      En Servicio
    </span>;
    };

    if (loading) {
        return (
            <Layout>
                <Loading message="Cargando activos..." />
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                    <div className="flex items-center space-x-3 mb-4 md:mb-0">
                        <Package className="h-8 w-8 text-blue-600" />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Gestión de Activos</h1>
                            <p className="text-gray-600">Administra todos los activos de la empresa</p>
                        </div>
                    </div>

                    <div className="flex space-x-3">

                        <Button
                            variant="outline"
                            onClick={() => setShowQRDownloadModal(true)}
                            className="flex items-center space-x-2 text-purple-600 border-purple-300 hover:bg-purple-50"
                            disabled={downloadProgress.isDownloading}
                        >   
                            <QrCode className="h-4 w-4" />
                            <span>Descargar QR</span>
                        </Button>


                        {!isLector() && (
                        <Button
                            variant="outline"
                            onClick={() => setShowUploadModal(true)}
                            className="flex items-center space-x-2"
                        >
                            <Upload className="h-4 w-4" />
                            <span>Importar Excel</span>
                        </Button>
                        )}

                        <Button
                            variant="outline"
                            onClick={handleExport}
                            className="flex items-center space-x-2"
                        >
                            <Download className="h-4 w-4" />
                            <span>Exportar</span>
                        </Button>

                        {!isLector() && (
                        <Button onClick={handleCreate} className="flex items-center space-x-2">
                            <Plus className="h-4 w-4" />
                            <span>Nuevo Activo</span>
                        </Button>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <Package className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">
                                    Total Activos {categoryFilter || statusFilter || ubicacionFilter ? '(Filtrados)' : ''}
                                </p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {totalItems}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <CheckCircle className="h-8 w-8 text-green-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">
                                    En Servicio
                                    {totalItems > itemsPerPage && <span className="text-xs block text-gray-500">(Página actual)</span>}
                                </p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {statsData.enServicio}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <XCircle className="h-8 w-8 text-red-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">
                                    Fuera de Servicio
                                    {totalItems > itemsPerPage && <span className="text-xs block text-gray-500">(Página actual)</span>}
                                </p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {statsData.fueraServicio}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <Clock className="h-8 w-8 text-yellow-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">
                                    Con QR
                                    {totalItems > itemsPerPage && <span className="text-xs block text-gray-500">(Página actual)</span>}
                                </p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {statsData.conQR}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex flex-col space-y-6">
                        {/* Primera fila: Campo de búsqueda grande */}
                        <div className="w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Buscar Activos
                            </label>
                            <div className="flex">
                                <div className="relative flex-1">
                                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar por código, nombre, fabricante o modelo..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                        className="w-full pl-12 pr-4 py-3 text-lg border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 focus:bg-white transition-colors"
                                    />
                                </div>
                                <button
                                    onClick={handleSearch}
                                    className="px-6 py-3 bg-blue-600 text-white border border-blue-600 rounded-r-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm"
                                    title="Buscar activos"
                                >
                                    <Search className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {/* Segunda fila: Filtros */}
                        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todas las categorías</option>
                                    {categorias.map(categoria => (
                                        <option key={categoria.id} value={categoria.nombre}>{categoria.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todos los estados</option>
                                    <option value="servicio">En servicio</option>
                                    <option value="fuera_servicio">Fuera de servicio</option>
                                </select>
                            </div>

                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                                <select
                                    value={ubicacionFilter}
                                    onChange={(e) => setUbicacionFilter(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todas las ubicaciones</option>
                                    {ubicaciones.map(ubicacion => (
                                        <option key={ubicacion.id} value={ubicacion.id}>
                                            {ubicacion.codigo} - {ubicacion.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        {/* Tercera fila: Selección múltiple */}
                        {selectedActivos.length > 0 && (
                            <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                                <span className="text-blue-700 font-medium">
                                    {selectedActivos.length} activo{selectedActivos.length !== 1 ? 's' : ''} seleccionado{selectedActivos.length !== 1 ? 's' : ''}
                                </span>
                                <div className="flex space-x-3">
                                    <Button
                                        onClick={handleDownloadSelectedQRs}
                                        disabled={downloadProgress.isDownloading}
                                        className="bg-green-600 hover:bg-green-700 text-white text-sm"
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        Descargar QR Seleccionados
                                    </Button>
                                    <Button
                                        onClick={() => setSelectedActivos([])}
                                        variant="outline"
                                        className="text-sm"
                                    >
                                        Limpiar Selección
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Activos Table */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Lista de Activos ({filteredActivos.length})
                            </h2>
                            {filteredActivos.length > 0 && (
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={selectedActivos.length === filteredActivos.length}
                                        onChange={handleSelectAll}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-600">Seleccionar todos</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                                    <input
                                        type="checkbox"
                                        checked={filteredActivos.length > 0 && selectedActivos.length === filteredActivos.length}
                                        onChange={handleSelectAll}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Código
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    ID Netsuite
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Nombre
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fabricante/Modelo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Categoría
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Etiquetado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ubicación
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {filteredActivos.map((activo) => (
                                <tr key={activo.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            checked={selectedActivos.includes(activo.id)}
                                            onChange={() => handleSelectActivo(activo.id)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <span className="text-sm font-medium text-gray-900">{activo.code}</span>
                                            {activo.urlcodeqr && (
                                                <QrCode className="ml-2 h-4 w-4 text-gray-400" />
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{activo.id_netsuite || '-'}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                            {activo.name}
                                            {activo.es_nuevo_ingreso && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    Nuevo
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900">{activo.factory}</div>
                                        <div className="text-sm text-gray-500">{activo.model}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900">
                                            {activo.categoria ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {activo.categoria}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">Sin categoría</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getStatusBadge(activo)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {activo.etiquetado ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                Sí
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                <XCircle className="w-3 h-3 mr-1" />
                                                No
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center text-sm text-gray-900">
                                            <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                                            {activo.ubicacion_obj ? `${activo.ubicacion_obj.codigo} - ${activo.ubicacion_obj.nombre}` : 'Sin ubicación'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handleView(activo)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title="Ver detalles"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => openQRModal(activo)}
                                                className="text-purple-600 hover:text-purple-900"
                                                title="Ver código QR"
                                            >
                                                <QrCode className="h-4 w-4" />
                                            </button>
                                              <button
                                                onClick={() => handleDownloadQR(activo)}
                                                className="text-green-600 hover:text-green-900"
                                                title="Descargar QR"
                                                disabled={downloadProgress.isDownloading}
                                            >
                                                <Download className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => openMantenimientoModal(activo)}
                                                className="text-orange-600 hover:text-orange-900"
                                                title="Mantenimiento / Vencimiento"
                                            >
                                                <Wrench className="h-4 w-4" />
                                            </button>

                                            {!isLector() && (
                                            <>
                                            <button
                                                onClick={() => handleEdit(activo)}
                                                className="text-green-600 hover:text-green-900"
                                                title="Editar"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(activo)}
                                                className="text-red-600 hover:text-red-900"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                            </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>

                        {filteredActivos.length === 0 && activos.length > 0 && (
                            <div className="text-center py-8">
                                <Filter className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay activos que coincidan</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    No se encontraron activos con los filtros aplicados
                                </p>
                            </div>
                        )}
                        
                        {activos.length === 0 && (
                            <div className="text-center py-8">
                                <Package className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay activos</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {searchTerm ? 'No se encontraron activos con esa búsqueda' : 'Comienza creando un nuevo activo'}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Paginación */}
                    {totalItems > 0 && (
                        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                            <PaginationControls
                                currentPage={currentPage}
                                totalItems={totalItems}
                                itemsPerPage={itemsPerPage}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </div>
                    )}
                </div>

                {/* Modal para Crear/Editar/Ver Activo */}
                {showModal && (
                    <Modal
                        title={
                            modalType === 'create' ? 'Nuevo Activo' :
                                modalType === 'edit' ? 'Editar Activo' :
                                    'Detalles del Activo'
                        }
                        onClose={() => setShowModal(false)}
                        size="lg"
                    >
                        {modalType === 'view' ? (
                            <ActivoDetails activo={selectedActivo} />
                        ) : (
                            <ActivoForm
                                formData={formData}
                                setFormData={setFormData}
                                onSubmit={handleSubmit}
                                isEdit={modalType === 'edit'}
                                onClose={() => setShowModal(false)}
                                categorias={categorias}
                                ubicaciones={ubicaciones}
                            />
                        )}
                    </Modal>
                )}
                
        {showQRDownloadModal &&  (
           <Modal
            title="Descargar Códigos QR"
            onClose={() => setShowQRDownloadModal(false)}
            size="md"
        >
            <div className="space-y-6">
                <p className="text-gray-600">
                    Selecciona el formato para descargar los códigos QR de todos los activos:
                </p>

                <div className="grid grid-cols-1 gap-4">
                    {/* Opción PDF */}
                    <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <FileText className="h-8 w-8 text-red-600" />
                                <div>
                                    <h3 className="font-semibold text-gray-900">Descargar como PDF</h3>
                                    <p className="text-sm text-gray-600">
                                        Todos los códigos QR en un documento PDF organizado para imprimir
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Ideal para imprimir y pegar en los activos
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={handleDownloadAllQRsPDF}
                                loading={downloadProgress.isDownloading && downloadProgress.type === 'pdf'}
                                disabled={downloadProgress.isDownloading}
                                className="bg-red-600 hover:bg-red-700"
                            >
                                <FileText className="h-4 w-4 mr-2" />
                                PDF
                            </Button>
                        </div>
                    </div>

                    {/* Opción ZIP */}
                    <div className="border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:bg-green-50 transition-colors">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <Archive className="h-8 w-8 text-green-600" />
                                <div>
                                    <h3 className="font-semibold text-gray-900">Descargar como ZIP</h3>
                                    <p className="text-sm text-gray-600">
                                        Archivos PNG individuales con información detallada
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Incluye archivos de información para cada activo
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={handleDownloadAllQRsZIP}
                                loading={downloadProgress.isDownloading && downloadProgress.type === 'zip'}
                                disabled={downloadProgress.isDownloading}
                                className="bg-green-600 hover:bg-green-700"
                            >
                                <Archive className="h-4 w-4 mr-2" />
                                ZIP
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Información:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                        <li>• PDF: Códigos QR organizados en tabla para impresión fácil</li>
                        <li>• ZIP: Archivos individuales con información completa del activo</li>
                        <li>• Solo se incluyen activos con códigos QR válidos</li>
                        <li>• Los códigos QR incluyen información del activo y enlace directo</li>
                    </ul>
                </div>

                <div className="flex justify-end">
                    <Button
                        onClick={() => setShowQRDownloadModal(false)}
                        variant="outline"
                        disabled={downloadProgress.isDownloading}
                    >
                        Cancelar
                    </Button>
                </div>
            </div>
        </Modal>
    )}


       {/* Componente de progreso de descarga */}
                <DownloadProgress
                    isDownloading={downloadProgress.isDownloading}
                    downloadType={downloadProgress.type}
                    progress={downloadProgress.progress}
                    error={downloadProgress.error}
                    success={downloadProgress.success}
                    onClose={clearDownloadProgress}
                />

                {/* Modal de importación de activos */}
                {showUploadModal && (
    <Modal
        title="Importar Activos desde Excel"
        onClose={() => setShowUploadModal(false)}
        size="lg"
    >
        <div className="space-y-6">
            {/* Sección de descarga de plantilla */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                        <FileText className="h-6 w-6 text-blue-600 mt-0.5" />
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">
                                Paso 1: Descarga la Plantilla
                            </h3>
                            <p className="text-blue-700 mb-3 text-sm">
                                Usa nuestra plantilla Excel con el formato correcto y ejemplos de datos. 
                                Incluye todas las columnas requeridas y instrucciones detalladas.
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={handleDownloadTemplate}
                        className="bg-blue-600 hover:bg-blue-700 text-white flex items-center space-x-2 ml-4"
                        loading={downloadingTemplate}
                    >
                        <Download className="h-4 w-4" />
                        <span>Plantilla</span>
                    </Button>
                </div>
            </div>

            {/* Sección de subida de archivo */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                    <Upload className="h-6 w-6 text-gray-600 mt-0.5" />
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Paso 2: Sube tu Archivo Completado
                        </h3>
                        <p className="text-gray-700 mb-4 text-sm">
                            Una vez completada la plantilla con tus datos, súbela aquí para importar los activos masivamente.
                        </p>

                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                            <div className="space-y-3">
                                <Upload className="mx-auto h-10 w-10 text-gray-400" />
                                <div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                    <Button
                                        onClick={() => fileInputRef.current?.click()}
                                        variant="outline"
                                        className="mb-2"
                                    >
                                        Seleccionar Archivo Excel
                                    </Button>
                                    <p className="text-xs text-gray-500">
                                        Formatos: .xlsx, .xls • Máximo: 10MB
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Información rápida */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                        <h4 className="font-medium text-yellow-800 mb-2">
                            Requisitos Importantes:
                        </h4>
                        <div className="text-yellow-700 grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                                <strong>• Columnas obligatorias:</strong><br/>
                                code, name, factory, model, ubicado
                            </div>
                            <div>
                                <strong>• Formatos especiales:</strong><br/>
                                Fechas: YYYY-MM-DD<br/>
                                Estado: TRUE/FALSE
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                    onClick={() => setShowUploadModal(false)}
                    variant="outline"
                >
                    Cancelar
                </Button>
            </div>
        </div>
    </Modal>
)}
                {/* Modal para mostrar QR del Activo */}
                {showQRModal && selectedActivoForQR && (
                    <Modal
                        title={`Código QR - ${selectedActivoForQR.code}`}
                        onClose={() => {
                            setShowQRModal(false);
                            setSelectedActivoForQR(null);
                        }}
                        size="md"
                    >
                        <QRDisplay
                            activo={selectedActivoForQR}
                            onClose={() => {
                                setShowQRModal(false);
                                setSelectedActivoForQR(null);
                            }}
                        />
                    </Modal>
                )}

                {/* Modal de Mantenimiento / Vencimiento del Activo */}
                {showMantenimientoModal && selectedActivoMantenimiento && (
                    <ModalMantenimientoActivo
                        activo={selectedActivoMantenimiento}
                        soloLectura={isLector()}
                        onClose={() => {
                            setShowMantenimientoModal(false);
                            setSelectedActivoMantenimiento(null);
                        }}
                    />
                )}

            </div>
        </Layout>
    );
};

// Componente para el formulario
const ActivoForm = ({ formData, setFormData, onSubmit, isEdit, onClose, categorias, ubicaciones }) => {
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    return (
        <form onSubmit={onSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Código {!isEdit && <span className="text-gray-500">(Se genera automáticamente)</span>}
                    </label>
                    <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        readOnly={!isEdit}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        placeholder={!isEdit ? "Se generará automáticamente" : "ACT-001"}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        ID Netsuite
                    </label>
                    <input
                        type="text"
                        name="id_netsuite"
                        value={formData.id_netsuite}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="NS-12345"
                        maxLength={100}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Generador Eléctrico"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fabricante <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="factory"
                        value={formData.factory}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Caterpillar"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Modelo <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="model"
                        value={formData.model}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="C15-500KW"
                    />
                </div>


                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Horas de Operación
                    </label>
                    <input
                        type="text"
                        name="hours"
                        value={formData.hours}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="1250"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        URL Código QR
                    </label>
                    <input
                        type="url"
                        name="urlcodeqr"
                        value={formData.urlcodeqr}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://qr.code/act001"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha de Compra
                    </label>
                    <input
                        type="date"
                        name="timeshopping"
                        value={formData.timeshopping}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* ─── Datos financieros (depreciación) ─────────────────────── */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Precio de Compra (S/)
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="precio_compra"
                        value={formData.precio_compra}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ej: 8500.00"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vida Útil (años)
                    </label>
                    <input
                        type="number"
                        min="1"
                        step="1"
                        name="vida_util_anios"
                        value={formData.vida_util_anios}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ej: 8"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Valor Residual (S/)
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="valor_residual"
                        value={formData.valor_residual}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ej: 500.00"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categoría
                    </label>
                    <select
                        name="categoria"
                        value={formData.categoria}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Seleccionar categoría...</option>
                        {categorias.map(categoria => (
                            <option key={categoria.id} value={categoria.nombre}>
                                {categoria.nombre}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ubicación
                    </label>
                    <select
                        name="ubicacion_id"
                        value={formData.ubicacion_id}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Seleccionar ubicación...</option>
                        {ubicaciones.map(ubicacion => (
                            <option key={ubicacion.id} value={ubicacion.id}>
                                {ubicacion.codigo} - {ubicacion.nombre}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        name="outservice"
                        checked={formData.outservice}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                        Fuera de Servicio
                    </label>
                </div>

                <div className="flex items-center">
                    <input
                        type="checkbox"
                        name="etiquetado"
                        checked={formData.etiquetado}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                        Etiquetado
                    </label>
                </div>

                <div className="flex items-center">
                    <input
                        type="checkbox"
                        name="es_nuevo_ingreso"
                        checked={formData.es_nuevo_ingreso}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                        Activo nuevo comprado
                    </label>
                </div>

                <div className="flex items-center">
                    <input
                        type="checkbox"
                        name="visible"
                        checked={formData.visible}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                        Visible
                    </label>
                </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                    Cancelar
                </Button>
                <Button type="submit">
                    {isEdit ? 'Actualizar' : 'Crear'} Activo
                </Button>
            </div>
        </form>
    );
};

// Componente para mostrar detalles
const ActivoDetails = ({ activo }) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Información General</h3>
                    <dl className="space-y-3">
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Código</dt>
                            <dd className="text-sm text-gray-900">{activo.code}</dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">ID Netsuite</dt>
                            <dd className="text-sm text-gray-900">{activo.id_netsuite || 'No especificado'}</dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Nombre</dt>
                            <dd className="text-sm text-gray-900">{activo.name}</dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Fabricante</dt>
                            <dd className="text-sm text-gray-900">{activo.factory}</dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Modelo</dt>
                            <dd className="text-sm text-gray-900">{activo.model}</dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Ubicación</dt>
                            <dd className="text-sm text-gray-900 flex items-center">
                                <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                                {activo.ubicacion_obj ? `${activo.ubicacion_obj.codigo} - ${activo.ubicacion_obj.nombre}` : 'Sin ubicación'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Categoría</dt>
                            <dd className="text-sm text-gray-900">
                                {activo.categoria ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {activo.categoria}
                                    </span>
                                ) : (
                                    'Sin categoría'
                                )}
                            </dd>
                        </div>
                    </dl>
                </div>

                <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Estado y Operación</h3>
                    <dl className="space-y-3">
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Estado</dt>
                            <dd className="text-sm text-gray-900">
                                {activo.outservice ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Fuera de Servicio
                  </span>
                                ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    En Servicio
                  </span>
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Horas de Operación</dt>
                            <dd className="text-sm text-gray-900 flex items-center">
                                <Clock className="h-4 w-4 mr-1 text-gray-400" />
                                {activo.hours || '0'} horas
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Etiquetado</dt>
                            <dd className="text-sm text-gray-900">
                                {activo.etiquetado ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Sí
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                        <XCircle className="w-3 h-3 mr-1" />
                                        No
                                    </span>
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Visible</dt>
                            <dd className="text-sm text-gray-900">
                                {activo.visible ? 'Sí' : 'No'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Código QR</dt>
                            <dd className="text-sm text-gray-900">
                                {activo.urlcodeqr ? (
                                    <a href={activo.urlcodeqr} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                                        <QrCode className="h-4 w-4 mr-1" />
                                        Ver QR
                                    </a>
                                ) : (
                                    'No disponible'
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Fecha de Compra</dt>
                            <dd className="text-sm text-gray-900">
                                {formatDate(activo.timeshopping)}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>
    );
};

// Componente de controles de paginación
const PaginationControls = ({ currentPage, totalItems, itemsPerPage, onPageChange, onItemsPerPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Generar array de números de página para mostrar
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 7;

        if (totalPages <= maxPagesToShow) {
            // Mostrar todas las páginas si son pocas
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Mostrar páginas con elipsis
            if (currentPage <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 3) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
        <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0 gap-4">
            {/* Información de items y selector de items por página */}
            <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{startItem}</span> a{' '}
                    <span className="font-medium">{endItem}</span> de{' '}
                    <span className="font-medium">{totalItems}</span> activos
                </div>
                {onItemsPerPageChange && (
                    <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700">Items por página:</label>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Controles de paginación */}
            <div className="flex items-center space-x-2">
                {/* Botón anterior */}
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                    Anterior
                </button>

                {/* Números de página */}
                <div className="hidden sm:flex space-x-1">
                    {pageNumbers.map((page, index) => (
                        page === '...' ? (
                            <span
                                key={`ellipsis-${index}`}
                                className="px-3 py-2 text-sm text-gray-500"
                            >
                                ...
                            </span>
                        ) : (
                            <button
                                key={page}
                                onClick={() => onPageChange(page)}
                                className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                    currentPage === page
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                }`}
                            >
                                {page}
                            </button>
                        )
                    ))}
                </div>

                {/* Indicador de página en móvil */}
                <div className="sm:hidden px-3 py-2 text-sm font-medium text-gray-700">
                    Página {currentPage} de {totalPages}
                </div>

                {/* Botón siguiente */}
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                    Siguiente
                </button>
            </div>
        </div>
    );
};

export default ActivosPage;